import React, { useEffect, useState, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAppText } from "@/contexts/AppTextContext";
import { Loader2, CheckCircle2, XCircle } from "lucide-react";

export default function CheckoutSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { t } = useAppText();
  const [state, setState] = useState("pending"); // pending|paid|expired|error
  const attempts = useRef(0);

  useEffect(() => {
    const sessionId = params.get("session_id");
    if (!sessionId) {
      setState("error");
      return;
    }
    const poll = async () => {
      attempts.current += 1;
      try {
        const { data } = await api.get(`/checkout/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setState("paid");
          setTimeout(() => navigate("/dashboard"), 1500);
          return;
        }
        if (data.status === "expired") {
          setState("expired");
          return;
        }
        if (attempts.current >= 8) {
          setState("error");
          return;
        }
        setTimeout(poll, 2000);
      } catch {
        if (attempts.current >= 8) setState("error");
        else setTimeout(poll, 2000);
      }
    };
    poll();
  }, [params, navigate]);

  return (
    <div className="min-h-[70vh] flex items-center justify-center p-6">
      <div className="card-surface p-10 max-w-md w-full text-center" data-testid="checkout-success-page">
        {state === "pending" && (
          <>
            <Loader2 className="w-12 h-12 text-[#FF8C00] mx-auto mb-4 animate-spin" />
            <h2 className="font-heading text-2xl font-bold text-[#1A1422] mb-2">{t("checkout.verifying_title")}</h2>
            <p className="text-[#5C5868]">{t("checkout.verifying_subtitle")}</p>
          </>
        )}
        {state === "paid" && (
          <>
            <CheckCircle2 className="w-14 h-14 text-[#10B981] mx-auto mb-4" />
            <h2 className="font-heading text-2xl font-bold text-[#1A1422] mb-2">{t("checkout.success_title")}</h2>
            <p className="text-[#5C5868]">{t("checkout.success_subtitle")}</p>
          </>
        )}
        {state === "expired" && (
          <>
            <XCircle className="w-14 h-14 text-[#EF4444] mx-auto mb-4" />
            <h2 className="font-heading text-2xl font-bold text-[#1A1422] mb-2">{t("checkout.expired_title")}</h2>
            <p className="text-[#5C5868] mb-4">{t("checkout.expired_subtitle")}</p>
            <button onClick={() => navigate("/catalog")} className="btn-primary">{t("checkout.expired_button")}</button>
          </>
        )}
        {state === "error" && (
          <>
            <XCircle className="w-14 h-14 text-[#EF4444] mx-auto mb-4" />
            <h2 className="font-heading text-2xl font-bold text-[#1A1422] mb-2">{t("checkout.error_title")}</h2>
            <p className="text-[#5C5868] mb-4">{t("checkout.error_subtitle")}</p>
            <button onClick={() => navigate("/dashboard")} className="btn-primary">{t("checkout.error_button")}</button>
          </>
        )}
      </div>
    </div>
  );
}
